import { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import sortBy from 'lodash.sortby';
import Drawer from '@components/Drawer';
import { Select } from '@components/Select';
import { Input } from '@components/Input';
import { SmsTemplate } from '@components/SmsTemplate';
import { alertActions, channelsActions } from '@actions';
import { sendMessageService } from '@services';
import {
    useTranslation,
    useYupValidationResolver,
    useSurveysOptions,
    useChannels,
    useLocations,
    useWhatsAppBusinessTemplates,
} from '@hooks';
import { CHANNEL } from '@constants/channels';
import { handleApiError } from '@helpers/api';
import { preparePhoneNumberForSubmit, setInitialPhoneNumberToForm } from '@helpers/phone';
import { sendInvitesUiActions } from '@actions';
import BulkForm from './BulkForm';
import { StyledRadioButtons, StyledPhoneInput } from './styles';

const FORM_TYPE = { PERSONAL: 1, BULK: 0 };

const SendInvites = ({ isMobile }) => {
    const { t } = useTranslation();

    const [bulk, setBulk] = useState({ list: [], selected: [] });
    const [isSmsTooLong, setSmsTooLong] = useState(false);

    const account = useSelector(state => state.account?.account);
    const channels = useSelector(state => state.channels.data);
    const { isOpen } = useSelector(state => state.sendInvitesUi);
    const dispatch = useDispatch();
    const { getSurveysOptionsByLocation } = useSurveysOptions();
    const { getChannelOptions, getChannelsByLocationId } = useChannels();
    const { getSelectLocationsOptions } = useLocations();
    const { getWhatsAppBusinessTemplatesOptionsByChannel } = useWhatsAppBusinessTemplates();
    const availableCompanies = useMemo(
        () => account?.companies?.filter(c => c.surveys?.length > 0) || [],
        [account?.companies],
    );

    const SendInviteFormSchema = yup.object().shape({
        locationId: yup.string().required('validation.required'),
        surveyTemplateId: yup.string().required('validation.required'),
        channelId: yup.string().required('validation.required'),
        userName: yup.string(),
        formType: yup.number(),
        phone: yup.string().when('formType', {
            is: FORM_TYPE.PERSONAL,
            then: yup.string().when('channelType', {
                is: channelType => channelType === CHANNEL.EMAIL,
                then: yup.string().required('validation.required').email('validation.wrongEmailFormat'),
                otherwise: yup.string().required('validation.required').validatePhone('validation.wrongPhoneFormat'),
            }),
        }),
        template: yup.string().required('validation.required'),
        channelType: yup.number(),
        whatsAppTemplateId: yup.string().when('channelType', {
            is: channelType => channelType === CHANNEL.WHATSAPP_BUSINESS,
            then: yup.string().required('validation.required'),
            otherwise: yup.string().notRequired(),
        }),
    });

    const resolver = useYupValidationResolver(SendInviteFormSchema);

    const {
        getValues,
        handleSubmit,
        setValue,
        control,
        reset,
        watch,
        clearErrors,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver: resolver,
        defaultValues: {
            locationId: availableCompanies[0]?.companyId || '',
            surveyTemplateId: '',
            userName: '',
            phone: '',
            template: '',
            channelId: '',
            channelType: CHANNEL.EMAIL,
            formType: FORM_TYPE.PERSONAL,
            whatsAppTemplateId: '',
        },
    });

    const selectedLocationId = watch('locationId');
    const formType = watch('formType');
    const isPersonal = formType === FORM_TYPE.PERSONAL;
    const userName = watch('userName');
    const template = watch('template');
    const channelType = watch('channelType');
    const selectedChannelId = watch('channelId');

    useEffect(() => {
        dispatch(channelsActions.get());
        dispatch(channelsActions.getWhatsAppBusinessTemplates());
    }, [dispatch]);

    const companiesOptions = useMemo(() => {
        const filterCb = location => location.surveys?.length > 0;
        const locations = getSelectLocationsOptions(filterCb);
        return locations.map(group => ({ ...group, children: sortBy(group.children, 'label') }));
    }, [availableCompanies]);

    const surveysOptions = useMemo(() => {
        const surveys = getSurveysOptionsByLocation(selectedLocationId);
        return sortBy(surveys, 'label');
    }, [selectedLocationId]);

    const channelsOptions = useMemo(() => {
        const availableChannels = getChannelsByLocationId(selectedLocationId);
        return getChannelOptions({ out: true }, availableChannels);
    }, [selectedLocationId, channels]);

    const whatsappTemplatesOptions = useMemo(() => {
        return getWhatsAppBusinessTemplatesOptionsByChannel(selectedChannelId, { hasButtons: true });
    }, [selectedChannelId]);

    useEffect(() => {
        if (channelsOptions.length) {
            const channel = channelsOptions[0];
            setValue('channelId', channel?.value || '', { shouldValidate: true, shouldDirty: true });
            const initialPhone =
                channel?.type === CHANNEL.WHATSAPP || channel?.type === CHANNEL.SMS
                    ? setInitialPhoneNumberToForm('', account?.locale)
                    : '';
            setValue('channelType', channel?.type);
            clearErrors('phone');
            setValue('phone', initialPhone, { shouldValidate: false, shouldDirty: true });
            if (channelType !== channel?.type) {
                setBulk({ list: [], selected: [] });
            }
        }
    }, [channelsOptions, setValue]);

    useEffect(() => {
        if (surveysOptions.length) {
            const survey = surveysOptions[0];
            setValue('surveyTemplateId', survey?.value || '', { shouldValidate: true, shouldDirty: true });
            setValue('template', survey?.message || '');
        }
    }, [surveysOptions, setValue]);

    const onSubmit = async () => {
        const { locationId, userName, phone, surveyTemplateId, channelId, channelType, whatsAppTemplateId } =
            getValues();
        const message =
            channelType === CHANNEL.WHATSAPP_BUSINESS
                ? ''
                : document.getElementById('sms')
                ? document.getElementById('sms').innerText
                : document.getElementById('smsWithOut').innerText;

        const isEmail = phone?.match(/^[A-Z]/i) || phone?.includes('@');
        const email = isEmail ? phone : '';
        const phoneNumber = !isEmail ? phone : '';

        if (isPersonal) {
            const [firstName = '', lastName = ''] = userName.split(' ');
            try {
                await sendMessageService.sendMessage({
                    phoneNumber: preparePhoneNumberForSubmit(phoneNumber),
                    email,
                    message,
                    firstName,
                    lastName,
                    surveyTemplateId,
                    locationId,
                    channelId,
                    whatsAppTemplateId,
                });
                dispatch(alertActions.success(t('alertMessages.sendSuccess')));
                const phone =
                    channelType === CHANNEL.WHATSAPP || channelType === CHANNEL.SMS
                        ? setInitialPhoneNumberToForm('', account?.locale)
                        : '';
                setValue('phone', phone, { shouldValidate: false, shouldDirty: true });
                setValue('userName', '');
            } catch (error) {
                handleApiError(error, 'alertMessages.sendFail', { delay: error.delay });
                if (['channel_configuration_error', 'channel_not_connected'].includes(error.errorCode)) {
                    setValue('channelId', '', { shouldDirty: true });
                    setValue('whatsAppTemplateId', '', { shouldDirty: true });
                    dispatch(channelsActions.get());
                }
            }
        } else {
            const preparedList = bulk.selected.map(item => {
                const isSendSms = item.phoneValid;
                const isSendEmail = !item.phoneValid && item.emailValid;
                return {
                    email: isSendEmail ? item.Email : '',
                    phoneNumber: isSendSms ? preparePhoneNumberForSubmit(item.Mobile_Number) : '',
                    message,
                    firstName: item.First_Name,
                    lastName: item.Last_Name,
                    surveyTemplateId,
                    locationId,
                    channelId,
                    whatsAppTemplateId,
                };
            });
            try {
                await sendMessageService.sendBulkMessages(preparedList);
                const message =
                    preparedList.length > 1
                        ? `${preparedList.length} ${t('SendInvites.messagesSent')}`
                        : t('SendInvites.messageSent');
                dispatch(alertActions.success(message));
            } catch (error) {
                handleApiError(error, 'alertMessages.sendFail', { delay: error.delay });
                if (['channel_configuration_error', 'channel_not_connected'].includes(error.errorCode)) {
                    setValue('channelId', '', { shouldDirty: true });
                    setValue('whatsAppTemplateId', '', { shouldDirty: true });
                    dispatch(channelsActions.get());
                }
            }
            setBulk({ list: [], selected: [] });
        }
    };

    const isDisabled = isSubmitting || !isValid || !isDirty || isSmsTooLong || (!bulk.selected.length && !isPersonal);
    const selectedLocationName = availableCompanies?.find(
        company => company.companyId === selectedLocationId,
    )?.locationName;

    return (
        <Drawer
            variant="persistent"
            anchor="right"
            open={isOpen}
            onClose={() => {
                reset();
                dispatch(sendInvitesUiActions.setOpen(false));
            }}
            title={t('SendInvites.sendSurveys')}
            primaryText={t('SendInvites.sendSurvey')}
            primaryDisabled={isDisabled}
            primaryAction={handleSubmit(onSubmit)}
        >
            <Controller
                control={control}
                name="locationId"
                render={({ field, fieldState: { error }, ref }) => (
                    <Select
                        {...field}
                        ref={ref}
                        onChange={({ target: { value } }) => {
                            setValue('locationId', value, { shouldValidate: true, shouldDirty: true });
                            setValue('channelId', '', { shouldDirty: true });
                            setValue('surveyTemplateId', '', { shouldDirty: true });
                            setValue('whatsAppTemplateId', '', { shouldDirty: true });
                        }}
                        options={companiesOptions}
                        required
                        label={t('common.location')}
                        fullWidth
                        style={{ marginBottom: '16px' }}
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        groupBased
                    />
                )}
            />
            <Controller
                control={control}
                name="surveyTemplateId"
                render={({ field, fieldState: { error }, ref }) => (
                    <Select
                        {...field}
                        ref={ref}
                        onChange={({ target: { value } }) => {
                            setValue('surveyTemplateId', value, { shouldValidate: true, shouldDirty: true });
                            const surveyMessage = surveysOptions.find(survey => survey.value === value)?.message;
                            setValue('template', surveyMessage);
                        }}
                        options={surveysOptions}
                        required
                        label={t('common.survey')}
                        fullWidth
                        style={{ marginBottom: '16px' }}
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                    />
                )}
            />
            <Controller
                control={control}
                name="channelId"
                render={({ field, fieldState: { error }, ref }) => (
                    <Select
                        {...field}
                        ref={ref}
                        onChange={({ target: { value } }) => {
                            setValue('channelId', value, { shouldValidate: true, shouldDirty: true });
                            const channel = channels.find(c => c.id === value || '');
                            const phone =
                                channel?.type === CHANNEL.WHATSAPP ||
                                channel?.type === CHANNEL.SMS ||
                                channel?.type === CHANNEL.WHATSAPP_BUSINESS
                                    ? setInitialPhoneNumberToForm('', account?.locale)
                                    : '';
                            setValue('channelType', channel?.type);
                            clearErrors('phone');
                            setValue('phone', phone, { shouldValidate: false, shouldDirty: true });
                            setBulk({ list: [], selected: [] });
                            setValue('whatsAppTemplateId', '');
                        }}
                        options={channelsOptions}
                        required
                        label={t('common.channel')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                    />
                )}
            />
            {!isMobile && (
                <StyledRadioButtons
                    id="pl-radiobuttons-send-invites"
                    value={formType}
                    onChange={value => {
                        clearErrors('phone');
                        setValue('formType', value, { shouldValidate: true });
                        setValue('userName', '');
                        if (value === 1 && bulk.selected.length > 0) {
                            setBulk({ list: [], selected: [] });
                        }
                    }}
                    options={[
                        { value: FORM_TYPE.PERSONAL, label: t('SendInvites.personal') },
                        { value: FORM_TYPE.BULK, label: t('SendInvites.bulk') },
                    ]}
                />
            )}
            {isPersonal ? (
                <>
                    <Controller
                        control={control}
                        name="userName"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field }}
                                label={t('common.firstName')}
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                sx={{ marginBottom: '16px' }}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="phone"
                        render={({ field, fieldState: { error }, ref }) =>
                            channelType === CHANNEL.EMAIL ? (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field }}
                                    label={t('common.email')}
                                    fullWidth
                                    required
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    sx={{ marginBottom: '16px' }}
                                />
                            ) : (
                                <StyledPhoneInput
                                    placeholder=""
                                    label={t('common.phoneNumber')}
                                    inputProps={{ name: 'phone' }}
                                    value={field.value}
                                    autoFormat
                                    onChange={value => {
                                        setValue('phone', value, { shouldValidate: true, shouldDirty: true });
                                    }}
                                    error={error ? t(error.message) : null}
                                />
                            )
                        }
                    />
                </>
            ) : (
                <BulkForm locale={account?.locale} bulk={bulk} setBulk={setBulk} channelType={channelType} />
            )}
            {channelType === CHANNEL.WHATSAPP_BUSINESS ? (
                <Controller
                    control={control}
                    name="whatsAppTemplateId"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            ref={ref}
                            onChange={({ target: { value } }) => {
                                setValue('whatsAppTemplateId', value, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                            }}
                            options={whatsappTemplatesOptions}
                            required
                            label={t('FeedbackOverview.template')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
            ) : (
                <SmsTemplate
                    smsContent={template || ''}
                    label={t('common.message', { count: 1 })}
                    setDisabled={content => setSmsTooLong(!!content)}
                    userName={userName}
                    location={selectedLocationName}
                    maxLength={0}
                />
            )}
        </Drawer>
    );
};

export default SendInvites;
